import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/brand-mark";
import { ThemeToggle } from "@/components/theme-toggle";
import { AuthDebug } from "@/components/auth-debug";

export default function LandingPage() {
  return (
    <div className="relative min-h-screen bg-background text-foreground bg-grain overflow-hidden">
      {/* Ambient brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-40 h-[520px] opacity-60 blur-3xl"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 40%, hsl(var(--brand) / 0.35), transparent 70%)",
        }}
      />

      <header className="relative z-10 flex items-center justify-between px-4 py-4 md:px-8 md:py-6">
        <BrandMark />
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="hidden text-sm text-muted-foreground transition-colors hover:text-foreground sm:inline"
          >
            Sign in
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-6 pt-24 pb-32 text-center">
        <div className="animate-fade-in-up inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground backdrop-blur">
          <Sparkles className="h-3.5 w-3.5 text-brand" strokeWidth={2.25} />
          <span>Video review, versions, and approval — for teams</span>
        </div>

        <h1
          className="animate-fade-in-up mt-8 font-display text-6xl font-black leading-[1.02] tracking-tight sm:text-7xl"
          style={{ animationDelay: "80ms" }}
        >
          Ship cuts faster.
          <br />
          <span className="text-muted-foreground">Review, resolved.</span>
        </h1>

        <p
          className="animate-fade-in-up mt-6 max-w-xl text-balance text-base leading-relaxed text-muted-foreground sm:text-lg"
          style={{ animationDelay: "160ms" }}
        >
          Frame-accurate comments, side-by-side versions, and one link that keeps
          clients on the same page. Built for editors who don't have time to babysit
          email threads.
        </p>

        <div
          className="animate-fade-in-up mt-10 flex flex-wrap items-center justify-center gap-3"
          style={{ animationDelay: "240ms" }}
        >
          <Button asChild size="lg" className="group">
            <Link to="/login">
              Sign in
              <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/register">Register</Link>
          </Button>
        </div>

        {/* Dev nav — quick jump to shell routes during scaffolding. Remove in Task 6. */}
        <div
          className="animate-fade-in-up mt-24 grid w-full max-w-2xl gap-3 rounded-2xl border border-border bg-card/60 p-6 text-left shadow-card backdrop-blur"
          style={{ animationDelay: "320ms" }}
        >
          <div className="flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground">
            <span>Route shell — dev preview</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              Live
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {[
              { to: "/login", label: "Login" },
              { to: "/invite/sample-token", label: "Invite" },
              { to: "/watch/sample-token", label: "Watch" },
              { to: "/app/projects", label: "Editor / app" },
              { to: "/admin/projects", label: "Admin" },
              { to: "/superadmin", label: "Superadmin" },
            ].map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="group inline-flex items-center justify-between gap-2 rounded-md border border-border bg-background/50 px-3 py-2 text-xs font-medium transition-colors hover:border-brand/40 hover:bg-brand-soft"
              >
                <span>{r.label}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
          <div className="mt-2 border-t border-border pt-3">
            <AuthDebug />
          </div>
        </div>
      </main>
    </div>
  );
}
